/**
 * defa.views.UsageCreateForm
 * A form to add new usages to invoices and counter invoices.
 * @version 1.2.3
 */
import { defa } from 'defa-object';
import Form     from 'scripts/poppet/form';
import template from 'templates/usageCreateForm.jst.html';

const selectors = {
	inputTotal       : '[data-input=create-total]',
	inputCent        : '[data-input=create-cent]',
	inputNote        : '[data-input=create-note]',
	inputType        : '[data-input=create-type]',
	inputEntire      : '[data-input=create-entire]',
	inputSubscription: '[data-input="select-subscription"]',
};

const UsageCreateForm = Form.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template: template,
	
	ui: {
		total       : selectors.inputTotal,
		cent        : selectors.inputCent,
		note        : selectors.inputNote,
		entire      : selectors.inputEntire,
		type        : selectors.inputType,
		subscription: selectors.inputSubscription,
	},
	
	events: {
		'change @ui.type'  : 'onChangeType',
		'change @ui.entire': 'onChangeEntire',
	},
	
	templateContext: function () {
		return {
			usageTypes   : this.getUsageTypes(),
			subscriptions: this.getSubscriptions(),
			remainingSum : this.getRemainingSum(),
		};
	},
	
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	/**
	 * Trigger when a user changes the entire-checkbox.
	 * @void
	 */
	onChangeEntire: function () {
		
		if ( this.ui.entire.is( ':checked' ) ) {
			this.ui.total.val( '' );
			this.ui.cent.val( '' );
			
			this.ui.total.prop( 'disabled', true );
			this.ui.cent.prop( 'disabled', true );
		} else {
			this.ui.total.removeAttr( 'disabled' );
			this.ui.cent.removeAttr( 'disabled' );
			
			this.ui.cent.val( '00' );
		}
	},
	
	/**
	 * Triggers when a user changes the type.
	 * @void
	 */
	onChangeType: function () {
		
		if ( 'CONCESSION' === this.ui.type.val().trim() ) {
			this.$el.addClass( 'is-concession' );
		} else {
			this.$el.removeClass( 'is-concession' );
			this.ui.entire.prop( 'checked', false );
			this.ui.total.removeAttr( 'disabled' );
			this.ui.cent.removeAttr( 'disabled' );
			this.ui.cent.val( '00' );
		}
	},
	
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	/**
	 * Gets the subscription id's from this invoices usages.
	 * @return {array} An array of unique subscription ids.
	 */
	getSubscriptions: function () {
		
		let usages = this.options.invoice.getUsages();
		return _.uniq( usages.pluck( 'subscription' ) );
	},
	
	/**
	 * Gets the remaining sum from the invoice.
	 * @return {string} The remaining sum from the invoice.
	 */
	getRemainingSum: function () {
		return this.options.invoice.get( 'transientFormatSumRemaining' );
	},
	
	/**
	 * Gets allowed usage types for new usages.
	 * @return {array} An array of usage types.
	 */
	getUsageTypes: function () {
		
		let allowed = [ 'CONCESSION', 'MANUAL' ],
			usages  = [];
		_.each( defa.vars.usageTypes, function ( usage ) {
			if ( -1 !== _.indexOf( allowed, usage.value ) ) {
				usages.push( usage );
			}
		} );
		return usages;
	},
	
	/**
	 * Gets the amount for the usage form.
	 * @return {number}  The sum of the total and the cent inputs
	 */
	getAmount: function () {
		let total    = parseInt( this.ui.total.val().trim() ),
			cent     = parseInt( this.ui.cent.val().trim() ),
			negative = -1 === Math.sign( total );
		
		let result = ( ( Math.abs( total ) * 100 ) + Math.abs( cent ) ) / 100;
		
		if ( !_.isNumber( result ) || _.isNaN( result ) ) {
			result = 0;
		}
		
		return ( negative ) ? -result : result;
	},
	
	/**
	 * Validates the form
	 * @return {bool}  True if validated, false if not.
	 */
	validate: function () {

		return ( this.ui.entire.is( ':checked' ) )
				  ? ( this.ui.type.val() )
				  : ( this.getAmount() && this.ui.type.val() );
	},
	
	/**
	 * Submits the form
	 * @void
	 */
	submit: function () {
		
		let form = {
			amount          : this.getAmount(),
			entire          : this.ui.entire.is( ':checked' ),
			note            : this.ui.note.val().trim(),
			type            : this.ui.type.val().trim(),
			subscription    : parseInt( this.ui.subscription.val().trim(), 10 ),
			transientInvoice: this.options.invoice.get( 'id' ),
		};
		
		this.model.save( form );
		
	},
	
} );

export default UsageCreateForm;